import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { SEO } from "@/components/SEO";
import { LOGO_URL } from "@/lib/constants";
import { CheckCircle2, XCircle, Loader2, ArrowLeft, ShieldCheck } from "lucide-react";

const TYPE_LABELS = { adherent: "Adhérent", sympathisant: "Sympathisant", benevole: "Bénévole" };
const STATUS = {
  valide: { label: "Adhésion validée", cls: "text-emerald-700 bg-emerald-50" },
  en_attente: { label: "En attente de validation", cls: "text-amber-700 bg-amber-50" },
  rejete: { label: "Adhésion rejetée", cls: "text-red-700 bg-red-50" },
};

export default function Verify() {
  const { number } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/verify/${encodeURIComponent(number)}`).then(({ data }) => setData(data)).catch(() => setData({ valid: false })).finally(() => setLoading(false));
  }, [number]);

  return (
    <div data-testid="verify-page" className="min-h-[80vh] bg-brand-surface flex items-center justify-center px-4 py-16">
      <SEO title="Vérification de carte membre" noindex />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="DEFAR" className="h-14 mx-auto" />
          <p className="mt-3 text-xs uppercase tracking-[0.2em] font-bold text-brand-green">Vérification d'adhésion</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand-blue" size={32} /></div>
        ) : data?.valid ? (
          <div data-testid="verify-valid" className="bg-white border border-border rounded-sm overflow-hidden">
            <div className="bg-brand-green text-white p-6 flex items-center gap-3">
              <CheckCircle2 size={28} />
              <div>
                <p className="font-bold text-lg">Membre authentique</p>
                <p className="text-sm text-emerald-100">Carte DEFAR - Les Bâtisseurs vérifiée</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <Row label="Nom" value={data.full_name} />
              <Row label="N° d'adhérent" value={data.member_number} mono />
              <Row label="Type" value={TYPE_LABELS[data.membership_type] || data.membership_type} />
              {data.region && <Row label="Région" value={data.region} />}
              <div className="pt-2">
                <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded ${(STATUS[data.status] || STATUS.en_attente).cls}`}>
                  <ShieldCheck size={15} /> {(STATUS[data.status] || STATUS.en_attente).label}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div data-testid="verify-invalid" className="bg-white border border-border rounded-sm p-8 text-center">
            <XCircle size={48} className="mx-auto text-red-500" />
            <h1 className="mt-4 text-xl font-bold text-slate-900">Carte non reconnue</h1>
            <p className="mt-2 text-sm text-slate-500">Le numéro « {number} » ne correspond à aucun membre enregistré. Méfiez-vous des cartes contrefaites.</p>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-blue">
            <ArrowLeft size={16} /> Retour au site
          </Link>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, mono }) => (
  <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-3 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className={`text-sm font-semibold text-slate-900 ${mono ? "font-mono" : ""}`}>{value}</span>
  </div>
);
